<template>
  <div>
    <div v-if="loading" class="text-center mt-10">
      <v-progress-circular
        color="primary"
        indeterminate
        size="100"
      ></v-progress-circular>
    </div>
    <template v-else>
      <v-dialog v-model="dialog">
        <v-card>
          <v-card-title>
            Modifica evento
          </v-card-title>

          <v-divider></v-divider>

          <v-card-text class="pb-0 pt-4">
            <event-form
              v-bind="{ editedItem, error }"
              :model-fk="{ show: showFk }"
            ></event-form>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn class="px-4" outlined color="primary" @click="closeDialog">
              Annulla
            </v-btn>
            <v-btn class="px-4" depressed color="primary" @click="save">
              Salva
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-row class="px-4">
        <v-col cols="12" sm="2">
          <v-text-field
            v-model="colSize"
            label="Numero di colonne occupate (su 12)"
            type="number"
          />
        </v-col>
        <v-col cols="12" sm="2">
          <v-text-field
            v-model="startTime"
            label="Orario di inizio"
            type="number"
            min="0"
            max="24"
          />
        </v-col>
        <v-col cols="12" sm="2">
          <v-checkbox
            v-model="eventKinds"
            label="Eventi pubblici"
            :value="1"
          ></v-checkbox>
        </v-col>
        <v-col cols="12" sm="2">
          <v-checkbox
            v-model="eventKinds"
            label="Prove"
            :value="2"
          ></v-checkbox>
        </v-col>
        <v-col cols="12" sm="2">
          <v-checkbox
            v-model="eventKinds"
            label="Accordature"
            :value="3"
          ></v-checkbox>
        </v-col>
      </v-row>

      <div class="mt-4 px-3 d-flex justify-space-between">
        <v-icon :disabled="currentIndex === 0" @click="goToDay(-1)">
          fa fa-chevron-left
        </v-icon>
        <strong>{{ currentDayHuman }}</strong>
        <v-icon
          :disabled="currentIndex === timeRange.length - 1"
          @click="goToDay(1)"
        >
          fa fa-chevron-right
        </v-icon>
      </div>

      <div class="d-flex flex-nowrap" style="overflow-x: scroll;">
        <template v-for="showPlace in showPlaces">
          <v-col
            v-if="placeHasEventsThisDay(showPlace.id, currentDay)"
            :key="showPlace.id"
            cols="12"
            :sm="colSize"
          >
            <v-calendar
              v-model="currentDay"
              type="day"
              :events="getAllEventsFor(showPlace.id)"
              :event-name="eventName"
              :event-color="eventColor"
              :first-time="startTime"
              interval-width="40"
              :interval-count="intervalCount"
              :short-intervals="false"
              locale="it"
              interval-minutes="30"
              interval-height="20"
              :interval-style="intervalStyle(showPlace.availabilities)"
              @click:event="editEvent"
            >
              <template v-slot:day-label-header>
                {{ showPlace.place__name }}
              </template>
            </v-calendar>
          </v-col>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import isWithinInterval from "date-fns/isWithinInterval"
import parseISO from "date-fns/parseISO"
import { cloneDeep, groupBy, some } from "@/lodash"
import { mapState } from "vuex"

import EventForm from "@/components/EventForm"

// import models from "@/models"

export default {
  components: { EventForm },
  data() {
    return {
      colSize: 4,
      currentIndex: 0,
      dialog: false,
      editedItem: {},
      editedId: -1,
      error: {},
      eventKinds: [1, 2, 3],
      startTime: 0,
    }
  },
  computed: {
    showFk() {
      return this.$route.params.id
    },
    currentDay() {
      return this.$formatDate(this.timeRange[this.currentIndex], "yyyy-MM-dd")
    },
    currentDayHuman() {
      return this.$formatDate(
        this.timeRange[this.currentIndex],
        "EEEE d MMMM y"
      )
    },
    intervalCount() {
      return 48 - parseInt(this.startTime) * 2
    },
    getEvents() {
      return groupBy(
        this.events
          .filter((e) => this.eventKinds.includes(e.kind))
          .map((e) => ({
            id: e.id,
            // needed to disambiguate regular events and instruments
            isEvent: true,
            name: e.concert__short_title || e.title,
            place: e.place,
            kind: e.kind,
            code: e.code,
            warnings: e.warnings,
            start: this.$formatDate(
              this.$parseDate(e.start_date),
              "yyyy-MM-dd HH:mm"
            ),
            end: this.$formatDate(
              this.$parseDate(e.end_date),
              "yyyy-MM-dd HH:mm"
            ),
          })),
        "place"
      )
    },
    timeRange() {
      if (this.events.length < 1) {
        return [new Date()]
      }

      // get start/end dates for all events
      const starts = this.events.map((e) =>
        this.$formatDate(this.$parseDate(e.start_date), "yyyy-MM-dd")
      )
      const ends = this.events.map((e) =>
        this.$formatDate(this.$parseDate(e.end_date), "yyyy-MM-dd")
      )

      // merge into a single array and deduplicate it to create the range
      const uniqueDates = starts
        .concat(ends)
        .filter((v, i, a) => a.indexOf(v) === i)
        .sort()
        .map((d) => this.$parseDate(d))

      return uniqueDates
    },

    ...mapState({
      loading(state) {
        return (
          state.events.pending.allEvents &&
          state.shows.pending.allShowPlaces &&
          state.shows.pending.allShowInstruments &&
          state.shows.pending.show
        )
      },
      show(state) {
        return state.shows.show
      },
      events(state) {
        return state.events.allEvents
      },
      showInstruments(state) {
        return state.shows.allShowInstruments
      },
      showPlaces(state) {
        return state.shows.allShowPlaces.sort((a, b) => a.order - b.order)
      },
    }),
  },
  mounted() {
    this.fetch()
  },
  methods: {
    getInstrumentsFor(showPlaceId) {
      let instrumentAvails = []
      this.showInstruments.forEach((i) => {
        i.availabilities.forEach((a) => {
          if (!(a.show_place === showPlaceId)) {
            return
          }
          instrumentAvails.push({
            name: i.instrument__name,
            start: this.$formatDate(
              this.$parseDate(a.start_date),
              "yyyy-MM-dd HH:mm"
            ),
            end: this.$formatDate(
              this.$parseDate(a.end_date),
              "yyyy-MM-dd HH:mm"
            ),
            color: "blue",
          })
        })
      })
      return instrumentAvails
    },
    getAllEventsFor(showPlaceId) {
      return this.get(this.getEvents, showPlaceId, []).concat(
        this.getInstrumentsFor(showPlaceId)
      )
    },
    placeHasEventsThisDay(showPlaceId, day) {
      return this.getAllEventsFor(showPlaceId).some(
        (event) =>
          // slice(0,10) == "2020-01-01"
          event.start.slice(0, 10) === day || event.end.slice(0, 10) === day
      )
    },
    fetch() {
      this.$store.dispatch("retrieveShow", {
        params: { id: this.showFk },
      })
      this.fetchEvents()
      this.$store.dispatch("listAllShowInstruments", {
        params: { show: this.showFk },
      })
      this.$store.dispatch("listAllShowPlaces", {
        params: { show: this.showFk },
      })
    },
    fetchEvents() {
      this.$store.dispatch("listAllEvents", {
        params: { show: this.showFk },
      })
    },
    editEvent(event) {
      if (!event.event.isEvent) {
        return
      }
      const itemId = event.event.id
      this.editedId = itemId
      this.editedItem = cloneDeep(
        this.events.find((item) => item.id === itemId)
      )
      this.dialog = true
    },
    closeDialog() {
      this.dialog = false
      this.$nextTick(() => {
        this.error = {}
      })
    },
    async save() {
      try {
        await this.$store.dispatch("updateEvent", {
          params: { id: this.editedId },
          data: this.editedItem,
        })
        this.closeDialog()
        this.$store.commit("core/setSnack", {
          level: "success",
          msg: "Salvato",
        })
        this.fetchEvents()
      } catch (error) {
        this.error = error.response.data
      }
    },
    eventName(event) {
      let nameItems = []

      if (event.input.code) {
        nameItems.push(`<b>${event.input.code}</b>`)
      }

      nameItems.push(event.input.name)

      if (event.input.warnings && event.input.warnings.length > 0) {
        nameItems.push(
          `${event.input.warnings.length} <span class="fa fa-exclamation-triangle"></span>`
        )
      }

      return nameItems.join(" – ")
    },
    eventColor(event) {
      if (event.color) {
        return event.color
      }

      const kindColors = {
        1: "green",
        2: "#ffc40c",
        3: "#A0A0A0",
      }
      return kindColors[event.kind]
    },
    /**
     * Set the style of an interval based on the place availabilities.
     * Takes the availabilities array as a parameter and returns a function
     * with one parameter (the interval). Needed because of V-Calendar's API
     * limitations.
     */
    intervalStyle(availabilities) {
      return (interval) => {
        const intervalDate = new Date(
          interval.year,
          // only the month date is 0-indexed!!!
          // i have no words
          interval.month - 1,
          interval.day,
          interval.hour,
          interval.minute
        )
        if (
          some(availabilities, (avail) => {
            return isWithinInterval(intervalDate, {
              start: parseISO(avail.start_date),
              end: parseISO(avail.end_date),
            })
          })
        ) {
          return { "background-color": "yellowgreen" }
        }
        return undefined
      }
    },
    goToDay(toIndex) {
      let newIndex
      if (this.currentIndex + toIndex < 0) {
        newIndex = 0
      } else if (this.currentIndex + toIndex > this.timeRange.length - 1) {
        newIndex = this.timeRange.length - 1
      } else {
        newIndex = this.currentIndex + toIndex
      }
      this.currentIndex = newIndex
    },
  },
}
</script>

<style>
.v-calendar-daily_head-weekday {
  display: none;
}
.v-calendar-daily_head-day-label {
  font-size: 0.8rem;
  height: 5rem;
  overflow: hidden;
}
</style>
